// GlobalStickyButtons.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  InputBase,
  alpha,
  useMediaQuery,
  List,
  ListItemButton,
  Divider,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import NorthWestRoundedIcon from "@mui/icons-material/NorthWestRounded";

import { getLangFromCookie } from "../../Utils/LangCookie";
import {
  buildSiteSearchIndex,
  searchSiteIndex,
  getBestSearchTarget,
  getSearchResultsUrl,
  getSearchTypeLabel,
} from "./SiteSearchIndex";

/* ----------------------------- helpers ----------------------------- */

function safeTrim(v) {
  return String(v ?? "").trim();
}

function normalizeLang(lang) {
  const s = String(lang || "").toUpperCase().trim();
  return s === "EN" || s === "JP" || s === "ZH" ? s : "ZH";
}

const GLOBAL_SEARCH_HISTORY_KEY = "seho:globalSearchHistory:b2b";

function readHistory() {
  try {
    const raw = localStorage.getItem(GLOBAL_SEARCH_HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr)
      ? arr
          .map((x) => safeTrim(x))
          .filter(Boolean)
          .slice(0, 20)
      : [];
  } catch {
    return [];
  }
}

function writeHistory(list) {
  try {
    const clean = (Array.isArray(list) ? list : [])
      .map((x) => safeTrim(x))
      .filter(Boolean)
      .slice(0, 20);
    localStorage.setItem(GLOBAL_SEARCH_HISTORY_KEY, JSON.stringify(clean));
  } catch {}
}

function pushHistory(q) {
  const v = safeTrim(q);
  if (!v) return;

  const prev = readHistory();
  const next = [v, ...prev.filter((x) => x.toLowerCase() !== v.toLowerCase())];

  writeHistory(next);
}

function removeFromHistory(q) {
  const v = safeTrim(q);
  const prev = readHistory();
  const next = prev.filter((x) => x.toLowerCase() !== v.toLowerCase());

  writeHistory(next);
  return next;
}

function filterHistory(list, query) {
  const q = safeTrim(query).toLowerCase();

  if (!q) return list.slice(0, 8);

  return list.filter((x) => x.toLowerCase().includes(q)).slice(0, 8);
}

function makeResultSubtitle(result, lang) {
  const typeLabel = getSearchTypeLabel(result?.type, lang);
  const subtitle = safeTrim(result?.subtitle);

  if (!subtitle) return typeLabel;
  if (subtitle === typeLabel) return typeLabel;
  if (subtitle.startsWith(`${typeLabel} ·`)) return subtitle;

  return `${typeLabel} · ${subtitle}`;
}

/* ------------------------------------------------------------------ */

export default function GlobalStickyButtons({
  onSearchSubmit,
  hideOnSelector,
  hideOnRef,
  zIndex = 1300,
  bottomOffsetPx,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const location = useLocation();
  const { pathname, search: locationSearch } = location;
  const navigate = useNavigate();

  const homeTo = "/";
  const memberTo = "/auth";

  const [openSearch, setOpenSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [hideButtons, setHideButtons] = useState(false);

  const [lang, setLang] = useState(() => normalizeLang(getLangFromCookie()));
  const [history, setHistory] = useState(() => readHistory());
  const [searchIndex, setSearchIndex] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchReady, setSearchReady] = useState(false);

  const inputRef = useRef(null);
  const sentinelLocalRef = useRef(null);

  const historySuggestions = useMemo(
    () => filterHistory(history, query),
    [history, query]
  );

  const siteSuggestions = useMemo(() => {
    const q = safeTrim(query);
    if (!q) return [];
    return searchSiteIndex(searchIndex, q, { limit: 12 });
  }, [searchIndex, query]);

  const showRecentSearches = !safeTrim(query);
  const showNoResults =
    safeTrim(query) && searchReady && !searchLoading && siteSuggestions.length === 0;

  // === Intersection logic (now also for desktop) ===
  useEffect(() => {
    const autoFooter =
      document.querySelector("#site-footer") ||
      document.querySelector("[data-footer]") ||
      document.querySelector("footer");

    const node =
      hideOnRef?.current ||
      (hideOnSelector ? document.querySelector(hideOnSelector) : null) ||
      autoFooter ||
      sentinelLocalRef.current;

    if (!node) return;

    const BTN = isMobile ? 56 : 56;
    const GAP = isMobile ? 12 : 20;
    const extra = BTN + GAP + 20;

    const observer = new IntersectionObserver(
      ([entry]) => setHideButtons(entry.isIntersecting),
      {
        threshold: 0,
        rootMargin: `0px 0px -${extra}px 0px`,
      }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hideOnRef, hideOnSelector, isMobile, pathname]);

  // Hard-close dialog whenever route changes
  useEffect(() => {
    if (!openSearch) return;

    setOpenSearch(false);
  }, [pathname, locationSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep search language synced with app language changes.
  useEffect(() => {
    const updateLang = () => {
      setLang(normalizeLang(getLangFromCookie()));
    };

    window.addEventListener("app:languageChanged", updateLang);
    return () => window.removeEventListener("app:languageChanged", updateLang);
  }, []);

  // Build the SehoPro-only search index when the dialog opens.
  useEffect(() => {
    if (!openSearch) return;

    let cancelled = false;

    setSearchLoading(true);
    setSearchReady(false);

    buildSiteSearchIndex({ lang })
      .then((index) => {
        if (cancelled) return;
        setSearchIndex(Array.isArray(index) ? index : []);
        setSearchReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSearchIndex([]);
        setSearchReady(true);
      })
      .finally(() => {
        if (cancelled) return;
        setSearchLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [openSearch, lang]);

  const handleGoHome = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(homeTo);
  };

  const handleMember = () => navigate(memberTo);

  const handleOpenSearch = () => {
    setLang(normalizeLang(getLangFromCookie()));
    setHistory(readHistory());
    setOpenSearch(true);

    window.requestAnimationFrame(() => {
      inputRef.current?.focus?.();
    });
  };

  const handleCloseSearch = () => {
    setOpenSearch(false);
  };

  const navigateToSearchTarget = (target, q) => {
    const url = safeTrim(target?.url);
    if (!url) return;

    navigate(url, {
      state: {
        fromGlobalSearch: true,
        searchQuery: safeTrim(q),
        searchResultType: target?.type || "",
      },
    });
  };

  const submitSearchWithValue = (value, selectedTarget = null) => {
    const q = safeTrim(value);

    if (!q) {
      handleCloseSearch();
      return;
    }

    pushHistory(q);
    setHistory(readHistory());
    setOpenSearch(false);

    window.requestAnimationFrame(() => {
      if (selectedTarget?.url) {
        navigateToSearchTarget(selectedTarget, q);
        return;
      }

      if (onSearchSubmit) {
        onSearchSubmit(q, () => {
          setOpenSearch(false);
        });
        return;
      }

      const results = searchSiteIndex(searchIndex, q, { limit: 12 });
      const bestTarget = getBestSearchTarget(results, q);

      if (bestTarget?.url) {
        navigateToSearchTarget(bestTarget, q);
        return;
      }

      navigate(getSearchResultsUrl(q), {
        state: {
          fromGlobalSearch: true,
          searchQuery: q,
        },
      });
    });
  };

  const submitSearch = () => submitSearchWithValue(query);

  const handleToggleSidebar = () => {
    window.dispatchEvent(
      new CustomEvent("app:toggleSidebar", { detail: { toggle: true } })
    );
  };

  const stickyBtnSize = { xs: 52, sm: 56 };
  const stickyGap = { xs: 1.5, sm: 6 };
  const offset = bottomOffsetPx ?? (isMobile ? 12 : 20);
  const stickyBottom = `calc(${offset}px + env(safe-area-inset-bottom, 0px))`;

  const buttons = [
    { label: "Home", onClick: handleGoHome, icon: <HomeRoundedIcon /> },
    { label: "Search", onClick: handleOpenSearch, icon: <SearchRoundedIcon /> },
    { label: "Member", onClick: handleMember, icon: <PersonRoundedIcon /> },
  ];

  const mobileExtra = isMobile
    ? [{ label: "More", onClick: handleToggleSidebar, icon: <MoreHorizIcon /> }]
    : [];

  const visibleButtons = [...buttons, ...mobileExtra];

  return (
    <>
      {/* Sticky buttons row */}
      {!hideButtons && (
        <Box
          sx={{
            position: "fixed",
            left: "50%",
            bottom: stickyBottom,
            transform: "translateX(-50%)",
            zIndex,
            display: "flex",
            alignItems: "center",
            gap: stickyGap,
            pointerEvents: "none",
          }}
        >
          {visibleButtons.map((b, i) => (
            <IconButton
              key={`${b.label}-${i}`}
              aria-label={b.label}
              onClick={b.onClick}
              disableRipple
              sx={{
                pointerEvents: "auto",
                width: { xs: stickyBtnSize.xs, sm: stickyBtnSize.sm },
                height: { xs: stickyBtnSize.xs, sm: stickyBtnSize.sm },
                borderRadius: "50%",
                bgcolor: "background.paper",
                opacity: "50%",
                color: "text.primary",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: 3,
                "&:hover": {
                  bgcolor: "action.hover",
                  boxShadow: 5,
                  opacity: "100%",
                },
                transition:
                  "box-shadow .15s ease, transform .15s ease, background-color .15s ease",
              }}
            >
              {b.icon}
            </IconButton>
          ))}
        </Box>
      )}

      {/* Search dialog */}
      <Dialog
        open={openSearch}
        onClose={handleCloseSearch}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
            overflow: "hidden",
            bgcolor: (t) => alpha(t.palette.background.paper, 0.96),
            boxShadow: (t) =>
              `0 18px 48px ${alpha(t.palette.common.black, 0.35)}`,
            backdropFilter: prefersReducedMotion
              ? "none"
              : "saturate(180%) blur(8px)",
            mx: { xs: 1.5, sm: 2 },
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: prefersReducedMotion ? "none" : "blur(6px)",
              backgroundColor: "rgba(0,0,0,0.28)",
            },
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.background.paper, 0.96)
                : alpha("#fff", 0.98),
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: { xs: 1.15, sm: 1.4 },
              display: "flex",
              alignItems: "center",
              gap: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <SearchRoundedIcon fontSize="medium" sx={{ opacity: 0.72 }} />

            <InputBase
              inputRef={inputRef}
              autoFocus
              placeholder="What are you looking for?"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitSearch();
                if (e.key === "Escape") handleCloseSearch();
              }}
              sx={{
                flex: 1,
                fontSize: { xs: 16, md: 18 },
                py: 0.5,
              }}
              inputProps={{ "aria-label": "search" }}
            />

            {searchLoading && (
              <CircularProgress
                size={18}
                thickness={5}
                sx={{ opacity: 0.72, mr: 0.5 }}
              />
            )}

            <IconButton
              aria-label="Submit search"
              onClick={submitSearch}
              size="small"
              sx={{ "&:hover": { bgcolor: "action.hover" } }}
            >
              <SearchRoundedIcon />
            </IconButton>

            <IconButton
              aria-label="Close search"
              onClick={handleCloseSearch}
              size="small"
              sx={{ "&:hover": { bgcolor: "action.hover" } }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              maxHeight: { xs: "62vh", sm: "58vh" },
              overflowY: "auto",
              py: 0.75,
            }}
          >
            {showRecentSearches && historySuggestions.length > 0 && (
              <>
                <Box
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    pt: 0.5,
                    pb: 0.75,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <HistoryRoundedIcon fontSize="small" sx={{ opacity: 0.72 }} />
                    <Typography variant="caption" sx={{ opacity: 0.72 }}>
                      Recent searches
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => {
                      try {
                        localStorage.removeItem(GLOBAL_SEARCH_HISTORY_KEY);
                      } catch {}
                      setHistory([]);
                    }}
                    sx={{ opacity: 0.72 }}
                    aria-label="clear recent searches"
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Divider />
              </>
            )}

            {showRecentSearches && historySuggestions.length === 0 && (
              <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
                <Typography sx={{ fontSize: 14, opacity: 0.72 }}>
                  Start typing to search SehoPro products, brands, categories,
                  news, and pages.
                </Typography>
              </Box>
            )}

            {showRecentSearches && historySuggestions.length > 0 && (
              <List dense disablePadding>
                {historySuggestions.map((s, idx) => (
                  <ListItemButton
                    key={`${s}-${idx}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => submitSearchWithValue(s)}
                    sx={{
                      px: { xs: 1.5, sm: 2 },
                      py: 1.05,
                      gap: 1.25,
                      "&:hover .seho-suggest-enter": { opacity: 1 },
                    }}
                  >
                    <HistoryRoundedIcon fontSize="small" sx={{ opacity: 0.64 }} />

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        noWrap
                        sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35 }}
                      >
                        {s}
                      </Typography>
                      <Typography
                        noWrap
                        sx={{ fontSize: 12.5, opacity: 0.68, lineHeight: 1.35 }}
                      >
                        Recent search
                      </Typography>
                    </Box>

                    <Box
                      className="seho-suggest-enter"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        opacity: 0.58,
                        transition: "opacity .12s ease",
                      }}
                    >
                      <NorthWestRoundedIcon fontSize="small" />
                    </Box>

                    <IconButton
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        const next = removeFromHistory(s);
                        setHistory(next);
                      }}
                      sx={{ ml: 0.25, opacity: 0.66 }}
                      aria-label="remove suggestion"
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </ListItemButton>
                ))}
              </List>
            )}

            {!showRecentSearches && siteSuggestions.length > 0 && (
              <>
                <Box
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    pt: 0.5,
                    pb: 0.75,
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.72 }}>
                    Search results inside SehoPro
                  </Typography>
                </Box>

                <Divider />

                <List dense disablePadding>
                  {siteSuggestions.map((result, idx) => (
                    <ListItemButton
                      key={`${result.id || result.url}-${idx}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => submitSearchWithValue(query, result)}
                      sx={{
                        px: { xs: 1.5, sm: 2 },
                        py: 1.1,
                        gap: 1.25,
                        "&:hover .seho-suggest-enter": { opacity: 1 },
                      }}
                    >
                      <SearchRoundedIcon fontSize="small" sx={{ opacity: 0.64 }} />

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          noWrap
                          sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35 }}
                        >
                          {result.title}
                        </Typography>
                        <Typography
                          noWrap
                          sx={{ fontSize: 12.5, opacity: 0.68, lineHeight: 1.35 }}
                        >
                          {makeResultSubtitle(result, lang)}
                        </Typography>
                      </Box>

                      <Box
                        className="seho-suggest-enter"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          opacity: 0.58,
                          transition: "opacity .12s ease",
                        }}
                      >
                        <NorthWestRoundedIcon fontSize="small" />
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              </>
            )}

            {showNoResults && (
              <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                  No matching content found
                </Typography>
                <Typography sx={{ fontSize: 13.5, opacity: 0.72 }}>
                  Press Enter to open the search results page for “{safeTrim(query)}”.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Fallback sentinel when no footer is found */}
      {!hideOnSelector && !hideOnRef && (
        <Box ref={sentinelLocalRef} sx={{ height: { xs: "18svh", sm: "10svh" } }} />
      )}
    </>
  );
}